import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { METIERS_LIST } from "@/data/metiers";
import { PRODUITS, PRODUITS_PRO_SLUGS, PRODUITS_PART_SLUGS } from "@/data/produits";

// Liens spéciaux non gérés par /produit/:slug (pages dédiées existantes)
const SPECIAL_PART_LINKS = [
  { to: "/assurance/emprunteur", label: "Assurance Emprunteur", id: "emprunteur" },
  { to: "/epargne-patrimoine", label: "PER • Épargne Retraite", id: "per" },
];

/**
 * Navbar v2 — bande blanche fixe (style Kretz) :
 *  • Logo OPTINA SVG noir à gauche
 *  • Menu hamburger rond à droite (entrée unique)
 *  • Drawer plein écran avec submenu accordéon Assurance ▾
 *  • CTA fixe "MON DEVIS EN − DE 24H"
 *
 * Structure menu :
 *   - Accueil
 *   - Assurance ▾
 *      • Assurance professionnelle (sous-titre petit)
 *         - Multirisque Pro / Local / Décennale BTP
 *         - RC Professionnelle
 *         - Flotte Automobile
 *         - Mutuelle Santé Collective
 *         - Prévoyance TNS
 *      • Particuliers (sous-titre petit)
 *         - Assurance Habitation
 *         - Assurance Auto / Moto
 *         - Mutuelle Santé
 *         - Assurance Emprunteur
 *   - Énergie
 *   - Épargne & PER
 *   - Blog
 *   - FAQ
 *   - Mentions légales
 */
const ASSURANCE_PRO_DEPRECATED = [];
const ASSURANCE_PART_DEPRECATED = [];

const MAIN_LINKS = [
  { to: "/", label: "Accueil", id: "accueil" },
  { to: "/energie", label: "Énergie", id: "energie" },
  {
    to: "/epargne-patrimoine",
    id: "epargne-patrimoine",
    label: (
      <>
        Épargne{" "}
        <span className="font-editorial italic font-normal text-[1.15em] leading-none">
          &amp;
        </span>{" "}
        PER
      </>
    ),
  },
  { to: "/blog", label: "Blog", id: "blog" },
  { to: "/faq", label: "FAQ", id: "faq" },
];

const SMALL_LINKS = [
  { to: "/mentions-legales", label: "Mentions légales", id: "mentions-legales" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [assurOpen, setAssurOpen] = useState(false);
  const [actOpen, setActOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Reset submenu state when drawer closes
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setAssurOpen(false);
        setActOpen(false);
      }, 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  const closeDrawer = () => setOpen(false);

  return (
    <>
      <header
        data-testid="main-navbar"
        className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-brand-border"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 md:h-[88px] flex items-center justify-between">
          <Link
            to="/"
            data-testid="logo-link"
            className="select-none flex items-center"
          >
            <img
              src="/logos/optina.svg"
              alt="OPTINA — Courtier indépendant en assurance et énergie"
              className="h-10 md:h-12 w-auto"
              width="220"
              height="48"
            />
          </Link>

          <button
            data-testid="navbar-menu-toggle"
            onClick={() => setOpen(true)}
            aria-label="Ouvrir le menu"
            className="h-11 w-11 rounded-full flex items-center justify-center bg-white border border-brand-border text-[#1A1A1A] hover:bg-brand-sand transition-colors"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </header>

      <div
        className={`fixed inset-0 z-[60] transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden={!open}
        data-testid="navbar-drawer"
      >
        <div className="absolute inset-0 bg-black/30" onClick={closeDrawer} />
        <aside
          className={`absolute right-0 top-0 h-full w-full sm:w-[440px] bg-brand-sand shadow-2xl flex flex-col transform transition-transform duration-300 ${
            open ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between px-6 md:px-8 h-20 md:h-[88px] border-b border-brand-border">
            <img src="/logos/optina.svg" alt="OPTINA" className="h-8 md:h-9 w-auto" />
            <button
              data-testid="drawer-close"
              onClick={closeDrawer}
              aria-label="Fermer le menu"
              className="h-10 w-10 rounded-full bg-white border border-brand-border flex items-center justify-center hover:bg-brand-sand-2"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-6 md:px-8 py-8 flex flex-col gap-1">
            {/* Accueil */}
            <NavLink
              to="/"
              onClick={closeDrawer}
              className={({ isActive }) =>
                `font-serif text-[26px] md:text-[30px] tracking-tight py-2 leading-tight ${
                  isActive ? "text-[#1A1A1A]" : "text-[#1A1A1A]/80 hover:text-[#1A1A1A]"
                }`
              }
              data-testid="drawer-link-accueil"
            >
              Accueil
            </NavLink>

            {/* Nos Assurances ▾ — accordion */}
            <button
              type="button"
              onClick={() => {
                setAssurOpen((v) => !v);
                setActOpen(false);
              }}
              aria-expanded={assurOpen}
              data-testid="drawer-toggle-assurance"
              className="font-serif text-[26px] md:text-[30px] tracking-tight py-2 leading-tight text-[#1A1A1A]/80 hover:text-[#1A1A1A] flex items-center gap-2 text-left"
            >
              Nos Assurances
              <ChevronDown
                className={`h-5 w-5 transition-transform duration-300 ${
                  assurOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            <div
              data-testid="drawer-submenu-assurance"
              className={`overflow-hidden transition-all duration-300 bg-white flex-shrink-0 ${
                assurOpen ? "max-h-[1200px] opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <div className="pl-3 mt-1 mb-2 space-y-4 border-l border-brand-border/80">
                {/* PRO */}
                <div className="pl-4">
                  <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#1A1A1A]/55 mt-2 mb-2">
                    Pro
                  </div>
                  <ul className="space-y-1.5">
                    {PRODUITS_PRO_SLUGS.map((sl, i) => (
                      <li key={sl}>
                        <Link
                          to={`/produit/${sl}`}
                          onClick={closeDrawer}
                          data-testid={`drawer-sublink-produit-pro-${i}`}
                          className="block text-[13.5px] text-[#1A1A1A]/80 hover:text-[#1A1A1A] py-0.5 leading-snug"
                        >
                          {PRODUITS[sl].label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* PARTICULIER */}
                <div className="pl-4">
                  <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#1A1A1A]/55 mt-2 mb-2">
                    Particulier
                  </div>
                  <ul className="space-y-1.5">
                    {PRODUITS_PART_SLUGS.map((sl, i) => (
                      <li key={sl}>
                        <Link
                          to={`/produit/${sl}`}
                          onClick={closeDrawer}
                          data-testid={`drawer-sublink-produit-part-${i}`}
                          className="block text-[13.5px] text-[#1A1A1A]/80 hover:text-[#1A1A1A] py-0.5 leading-snug"
                        >
                          {PRODUITS[sl].label}
                        </Link>
                      </li>
                    ))}
                    {SPECIAL_PART_LINKS.map((l, i) => (
                      <li key={l.id}>
                        <Link
                          to={l.to}
                          onClick={closeDrawer}
                          data-testid={`drawer-sublink-${l.id}`}
                          className="block text-[13.5px] text-[#1A1A1A]/80 hover:text-[#1A1A1A] py-0.5 leading-snug"
                        >
                          {l.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Votre activité ▾ — 11 métiers */}
            <button
              type="button"
              onClick={() => {
                setActOpen((v) => !v);
                setAssurOpen(false);
              }}
              aria-expanded={actOpen}
              data-testid="drawer-toggle-activite"
              className="font-serif text-[26px] md:text-[30px] tracking-tight py-2 leading-tight text-[#1A1A1A]/80 hover:text-[#1A1A1A] flex items-center gap-2 text-left"
            >
              Votre activité
              <ChevronDown
                className={`h-5 w-5 transition-transform duration-300 ${
                  actOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            <div
              data-testid="drawer-submenu-activite"
              className={`overflow-hidden transition-all duration-300 flex-shrink-0 ${
                actOpen ? "max-h-[900px] opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <ul className="pl-7 mt-1 mb-2 space-y-1.5 border-l border-brand-border/80">
                {METIERS_LIST.map((m, i) => (
                  <li key={m.slug}>
                    <Link
                      to={`/metier/${m.slug}`}
                      onClick={closeDrawer}
                      data-testid={`drawer-sublink-metier-${i}`}
                      className="block text-[13.5px] text-[#1A1A1A]/75 hover:text-[#1A1A1A] py-0.5 leading-snug"
                    >
                      {m.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Reste du menu */}
            {MAIN_LINKS.filter((l) => l.id !== "accueil").map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                onClick={closeDrawer}
                className={({ isActive }) =>
                  `font-serif text-[26px] md:text-[30px] tracking-tight py-2 leading-tight ${
                    isActive ? "text-[#1A1A1A]" : "text-[#1A1A1A]/80 hover:text-[#1A1A1A]"
                  }`
                }
                data-testid={`drawer-link-${l.id}`}
              >
                {l.label}
              </NavLink>
            ))}

            <div className="mt-3 space-y-2">
              {SMALL_LINKS.map((l) => (
                <NavLink
                  key={l.id}
                  to={l.to}
                  onClick={closeDrawer}
                  className={({ isActive }) =>
                    `block py-1.5 text-sm tracking-wide ${
                      isActive
                        ? "text-[#1A1A1A] font-semibold"
                        : "text-[#1A1A1A]/65 hover:text-[#1A1A1A]"
                    }`
                  }
                  data-testid={`drawer-link-${l.id}`}
                >
                  {l.label}
                </NavLink>
              ))}
            </div>

            <Link
              to="/rendez-vous"
              data-testid="drawer-quote-btn"
              onClick={closeDrawer}
              className="btn-press mt-10 self-start inline-flex items-center gap-2 rounded-full bg-[#1A1A1A] hover:bg-black text-white px-6 py-3.5 text-[12px] font-semibold tracking-[0.22em] uppercase"
            >
              Mon devis en − de 24h
            </Link>
          </nav>

          <div className="px-6 md:px-8 py-6 border-t border-brand-border text-[12px] text-[#1A1A1A]/55 leading-relaxed">
            <p className="font-semibold text-[#1A1A1A]">OPTINA — Nassim Aali</p>
            <p>Courtier indépendant — ORIAS n° 26008264</p>
            <a href="tel:+33615907316" className="block mt-2 hover:text-[#1A1A1A]">
              +33 6 15 90 73 16
            </a>
          </div>
        </aside>
      </div>
    </>
  );
}
